import { useState, useEffect } from 'react'
import { NavLink, useNavigate, Outlet } from 'react-router-dom'
import ErrorBoundary from './ErrorBoundary'
import {
  MessageSquare, Image, Video, Music, Bot, Code, User, Shield,
  ChevronLeft, ChevronRight, LogOut, Settings, Sparkles,
  Menu, X, Coins, Gift, Users, Archive, Trash2,
  Languages, Table
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import { useChatStore } from '../store/chatStore'
import client from '../api/client'
// import { tokensToStars, formatBalance } from '../utils/stars'  // replaced with inline

const menuItems = [
  { path: '/chat', icon: MessageSquare, label: 'Чат', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  { path: '/image', icon: Image, label: 'Изображения', color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  { path: '/video', icon: Video, label: 'Видео', color: 'text-red-400', bgColor: 'bg-red-500/10' },
  { path: '/music', icon: Music, label: 'Музыка', color: 'text-pink-400', bgColor: 'bg-pink-500/10' },
  { path: '/sheets', icon: Table, label: 'Таблицы', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  { path: '/translator', icon: Languages, label: 'Переводчик', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  { path: '/characters', icon: Bot, label: 'Персонажи', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  { path: '/code', icon: Code, label: 'Код', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  { path: '/agents', icon: Sparkles, label: 'ИИ-агенты', color: 'text-indigo-400', bgColor: 'bg-indigo-500/10' },
]

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [chatPanelOpen, setChatPanelOpen] = useState(false)
  const [chatArchiveOpen, setChatArchiveOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const { balance, totalUsed, starsBalance, fetchBalance } = useTokenStore()
  const { chats, selectChat, deleteChat, archiveChat, unarchiveChat, currentChat } = useChatStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) fetchBalance()
  }, [user, fetchBalance])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-[#0f0f1a] overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative z-50 h-screen lg:h-screen max-lg:h-[100dvh]
          bg-[#0a0a15] border-r border-white/5
          transition-all duration-300 ease-in-out
          flex flex-col
          ${collapsed ? 'w-[72px]' : 'w-[260px]'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <a href="/" className={`flex items-center gap-3 px-4 h-16 border-b border-white/5 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          {!collapsed && (
            <span className="text-lg font-bold gradient-text whitespace-nowrap">
              Chat-Neuro
            </span>
          )}
        </a>

        {/* Navigation (весь сайдбар скроллится, включая низ) */}
        <nav className="flex-1 py-2 px-2 overflow-y-auto overflow-x-hidden max-lg:overscroll-contain">
          <div className="space-y-0.5">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-1.5 rounded-xl
                  transition-all duration-200 group relative
                  ${collapsed ? 'justify-center' : ''}
                  ${isActive
                    ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                  }
                `}
              >
                <div className={`p-1.5 rounded-lg ${collapsed ? '' : item.bgColor}`}>
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                </div>
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                    {item.label}
                  </div>
                )}
              </NavLink>
            ))}
          </div>

          {/* Нижняя секция — внутри скролла на мобилках, чтоб не терялась за панелью браузера */}
          <div className="mt-8 pt-4 border-t border-white/5 space-y-1 max-lg:pb-4 max-lg:mt-8 max-lg:pt-4">
            {/* Balance → Payment */}
            <NavLink
              to="/payment"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-1.5 rounded-xl transition-all duration-200
                ${collapsed ? 'justify-center' : ''}
                ${isActive ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}
              `}
            >
              {collapsed ? (
                <div className="flex flex-col items-center">
                  <div className="p-1.5 rounded-lg bg-amber-500/10 mb-0.5">
                    <Coins className="w-4 h-4 text-amber-400" />
                  </div>
                  <p className="text-[9px] font-semibold text-amber-300 leading-tight">{balance}</p>
                  <p className="text-[6px] text-gray-500 leading-tight">✦{starsBalance}</p>
                </div>
              ) : (
                <>
                  <div className="p-1.5 rounded-lg bg-amber-500/10">
                    <Coins className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Баланс</p>
                    <p className="text-xs font-semibold text-amber-300 break-words leading-tight">
                      {balance.toLocaleString()} CN / ✦{starsBalance.toLocaleString()}
                    </p>
                  </div>
                </>
              )}
            </NavLink>

            {/* Profile */}
            <NavLink
              to="/profile"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-1.5 rounded-xl transition-all duration-200
                ${collapsed ? 'justify-center' : ''}
                ${isActive ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}
              `}
            >
              <div className={`shrink-0 ${collapsed ? '' : 'p-0.5'} rounded-lg bg-indigo-500/10 overflow-hidden`}>
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name}
                    className={`${collapsed ? 'w-7 h-7' : 'w-8 h-8'} rounded-lg object-cover`}
                  />
                ) : (
                  <div className={`${collapsed ? 'w-7 h-7' : 'w-8 h-8'} flex items-center justify-center`}>
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                )}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || 'Профиль'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              )}
            </NavLink>

            {/* Admin */}
            {user?.is_admin && (
              <NavLink
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-1.5 rounded-xl transition-all duration-200
                  ${collapsed ? 'justify-center' : ''}
                  ${isActive ? 'bg-red-500/10 text-red-300' : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}
                `}
              >
                <div className="p-1 rounded-lg bg-red-500/10">
                  <Shield className="w-4 h-4 text-red-400" />
                </div>
                {!collapsed && <span className="text-sm font-medium">Админ</span>}
              </NavLink>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`flex items-center gap-3 px-3 py-1.5 rounded-xl transition-all duration-200 w-full text-gray-400 hover:text-red-400 hover:bg-red-500/5 ${collapsed ? 'justify-center' : ''}`}
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && <span className="text-sm font-medium">Выйти</span>}
            </button>
          </div>
        </nav>

        {/* Collapse toggle - desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-8 border-t border-white/5 text-gray-500 hover:text-gray-300 transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-white/5 bg-[#0f0f1a]">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <a href="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-bold gradient-text">Chat-Neuro</span>
          </a>
          <button
            onClick={() => setChatPanelOpen(true)}
            className="p-2 text-gray-400 hover:text-gray-200 rounded-lg hover:bg-white/5 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="sr-only">История чатов</span>
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Right chat panel overlay — mobile only */}
      {chatPanelOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setChatPanelOpen(false)}
          />
          <div className="fixed right-0 top-0 bottom-0 w-72 z-50 bg-[#0a0a15] border-l border-white/5 flex flex-col animate-slide-in-right lg:hidden">
            <div className="flex items-center justify-between p-3 border-b border-white/5">
              <span className="text-sm font-semibold text-gray-200">История чатов</span>
              <button
                onClick={() => setChatPanelOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {chats.filter(c => !c.archived).length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-600">Нет диалогов</p>
                </div>
              ) : (
                chats.filter(c => !c.archived).map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => {
                      selectChat(chat.id)
                      navigate(`/chat/${chat.id}`)
                      setChatPanelOpen(false)
                    }}
                    className={`
                      w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all group
                      ${currentChat?.id === chat.id
                        ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate flex-1">{chat.title}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); archiveChat(chat.id) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-amber-400 transition-all"
                          title="В архив"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteChat(chat.id) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-gray-600">{chat.model}</span>
                      <span className="text-[10px] text-gray-600">{chat.tokensUsed} токенов</span>
                    </div>
                  </button>
                ))
              )}
            </div>


            {/* Archived */}
            {chats.filter(c => c.archived).length > 0 && (
              <div className="border-t border-white/5 bg-[#0a0a15]">
                <button
                  onClick={() => setChatArchiveOpen(!chatArchiveOpen)}
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-[10px] text-gray-600 font-medium hover:bg-white/5 transition-all"
                >
                  <Archive className="w-3 h-3" />
                  <span>Архив</span>
                  <span className="ml-1 text-[10px] text-gray-600">({chats.filter(c => c.archived).length})</span>
                  <svg
                    className={`w-3 h-3 ml-auto transition-transform ${chatArchiveOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {chatArchiveOpen && (
                  <div className="px-2 pb-2 space-y-1">
                    {chats.filter(c => c.archived).map((chat) => (
                      <button
                        key={chat.id}
                        onClick={() => {
                          unarchiveChat(chat.id)
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs transition-all group flex items-center justify-between text-gray-500 hover:text-gray-300 hover:bg-white/5"
                      >
                        <span className="truncate flex-1">{chat.title}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteChat(chat.id) }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
